import { useMemo } from 'react';
import { useSelector } from 'react-redux';
import type { RootState } from 'redux/store';
import { useGetUserQuery } from 'services/api/user.api';
import type { User } from 'services/types/user';

export const useCurrentUserState = (): { user: User | null; isLoading: boolean } => {
  const persistedUserToken = useSelector<RootState, RootState['user']['accessToken']>(
    (state) => state.user?.accessToken || ''
  );
  const { data: updatedUser, isLoading } = useGetUserQuery(null, {
    skip: !persistedUserToken,
  });
  const persistedUser = useSelector<RootState, RootState['user']>((state) => state.user);

  return useMemo(
    () => ({
      user: updatedUser
        ? {
            ...updatedUser,
            accessToken: persistedUserToken,
          }
        : persistedUser,
      // On a cold load Redux contains the last persisted profile while /user is still
      // fetching. That profile is not authoritative for role checks.
      isLoading: Boolean(persistedUserToken) && isLoading,
    }),
    [updatedUser, persistedUserToken, persistedUser, isLoading]
  );
};

const useCurrentUser = (): User | null => useCurrentUserState().user;

export default useCurrentUser;
